import { DatePicker, Form, ConfigProvider, Select, Space, Button, Checkbox, Tag } from 'antd'
import { SearchOutlined, UndoOutlined, ApiOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import vi_VN from 'antd/es/locale/vi_VN'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import 'dayjs/locale/vi'
import { useCallback, useEffect, useState } from 'react'
import ChartMonth from '../../components/chart/ChartMonth'
import { useLoading } from '../../context/LoadingContext'
import { apiGetListCompanies, apiGetListUsers, apiGetStatisticalTelesaleCalling } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import { filterOptionByLabel } from '../../utils/filter-utils'
import ChartAmountType from '../../components/chart/ChartAmountType'
import { formatNumber } from '../../utils/string-utils'
import { IUser } from '../../models/user.model'
import { typeNoteCustomerOptions } from '../../assets/constants/default_options'
import { ICompany } from '../../models/company.modal'
import ChartTypeY from '../../components/chart/ChartTypeY'
import ChartDaySelect from '../../components/chart/ChartDaySelect'
import { colorPalette } from '../../utils/color-utils'

const { RangePicker } = DatePicker

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.locale('vi')

export default function TelesaleOverview() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [userOptions, setUserOptions] = useState<{ label: string, value: Number }[]>([])
    const [totalRecord, setTotalRecord] = useState<any>({})
    const [totalType, setTotalType] = useState<any>({})
    const [totalCpCode, setTotalCpCode] = useState<any>({})
    const [totalOwnerBy, setTotalOwnerBy] = useState<any>({})
    const [totalMonth, setTotalMonth] = useState<any>([])
    const [totalDaySuccess, setTotalDaySuccess] = useState<any>([])
    const [totalDayFail, setTotalDayFail] = useState<any>([])
    const [allMonthsHaving, setAllMonthsHaving] = useState<number[]>([])
    const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
    const [monthColorMap, setMonthColorMap] = useState<any>({});
    const [search, setSearch] = useState<any>({})
    const [defaultSearch, setDefaultSearch] = useState<any>()
    const [formSearch] = Form.useForm()
    const { setIsLoading } = useLoading()

    const fetchOptions = useCallback(async () => {
        try {
            const [respUser, respCompany] = await Promise.all([
                apiGetListUsers({
                    limit: defaultParams.MAX_LIMIT,
                }),
                apiGetListCompanies({
                    limit: defaultParams.MAX_LIMIT,
                }),
            ])
            if (respUser.data?.users) {
                const userOptions = respUser.data.users
                    .map((item: IUser) => ({
                        label: item.username,
                        value: item.username,
                    }))
                setUserOptions(userOptions)
            }
            if (respCompany.data?.companies) {
                const compOptions = respCompany.data.companies
                    .map((item: ICompany) => ({
                        label: item.name,
                        value: item.code,
                    }))
                setCompanyOptions(compOptions)
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    function sumarizeTotalRecord(data: any) {
        const summary: any = {
            total: 0,
            success: 0,
            fail: 0,
        };
        for (const item of data) {
            const { amount_type, amount } = item;
            summary.total += amount;
            if (amount_type === 'SUCCESS') {
                summary.success += amount;
            } else {
                summary.fail += amount;
            }
        }
        return summary;
    }

    function summarizeByFieldName(data: any, fieldName: string) {
        const summary: any = {};
        for (const item of data) {
            const { [fieldName]: fieldValue, amount } = item;
            summary[fieldValue] = (summary[fieldValue] || 0) + amount;
        }
        return Object.entries(summary).map(([type, sum]) => ({ type, sum }));
    }

    function summarizeByMonth(data: any) {
        const summary: any = {};
        for (const item of data) {
            const { month, amount } = item;
            summary[month] = (summary[month] || 0) + amount;
        }
        const months = Object.entries(summary).map(([month, sum]) => ({ month: parseInt(month), sum }));
        months.sort((a, b) => a.month - b.month);
        return months
    }

    function getAllMonths(data: any[]): number[] {
        const monthsSet = new Set<number>();
        for (const item of data) {
            if (item.month >= 1 && item.month <= 12) {
                monthsSet.add(item.month);
            }
        }
        const allMonths = Array.from(monthsSet).sort((a, b) => a - b);
        return allMonths
    }

    function summarizeByDaySuccess(data: any, allMonths: number[]) {
        const summary: any = {};
        for (const item of data) {
            const { month, day, amount, amount_type } = item;
            if (amount_type !== 'SUCCESS') continue;
            if (!summary[month]) {
                summary[month] = {
                    sum: 0,
                    days: Array.from({ length: 31 }, (_, i) => ({ day: i + 1, sum: 0 }))
                };
            }
            summary[month].sum += amount;
            if (day >= 1 && day <= 31) {
                summary[month].days[day - 1].sum += amount;
            }
        }
        const months = allMonths.map(month => {
            if (!summary[month]) {
                summary[month] = {
                    sum: 0,
                    days: Array.from({ length: 31 }, (_, i) => ({ day: i + 1, sum: 0 }))
                };
            }
            return {
                month,
                sum: summary[month].sum,
                days: summary[month].days
            };
        });
        return months;
    }

    function summarizeByDayFail(data: any, allMonths: number[]) {
        const summary: any = {};
        for (const item of data) {
            const { month, day, amount, amount_type } = item;
            if (amount_type === 'SUCCESS') continue;
            if (!summary[month]) {
                summary[month] = {
                    sum: 0,
                    days: Array.from({ length: 31 }, (_, i) => ({ day: i + 1, sum: 0 }))
                };
            }
            summary[month].sum += amount;
            if (day >= 1 && day <= 31) {
                summary[month].days[day - 1].sum += amount;
            }
        }
        const months = allMonths.map(month => {
            if (!summary[month]) {
                summary[month] = {
                    sum: 0,
                    days: Array.from({ length: 31 }, (_, i) => ({ day: i + 1, sum: 0 }))
                };
            }
            return {
                month,
                sum: summary[month].sum,
                days: summary[month].days
            };
        });
        return months;
    }

    const fetchStatistics = useCallback(async () => {
        try {
            setIsLoading(true)
            const { data } = await apiGetStatisticalTelesaleCalling(search)
            if (data?.statistics != null) {
                const allMonths = getAllMonths(data.statistics)
                setAllMonthsHaving(allMonths);
                setMonthColorMap(Object.fromEntries(
                    allMonths.map((month, index) => [month, colorPalette[index % colorPalette.length]])
                ))
                const sMonths = allMonths.slice(-2)
                setSelectedMonths(sMonths)
                setTotalRecord(sumarizeTotalRecord(data.statistics))
                setTotalDaySuccess(summarizeByDaySuccess(data.statistics, allMonths))
                setTotalDayFail(summarizeByDayFail(data.statistics, allMonths))
                setTotalCpCode(summarizeByFieldName(data.statistics, 'cpCode'))
                setTotalOwnerBy(summarizeByFieldName(data.statistics, 'owner_by'))
                setTotalType(summarizeByFieldName(data.statistics, 'amount_type'))
                setTotalMonth(summarizeByMonth(data.statistics))
            } else {
                setAllMonthsHaving([])
                setSelectedMonths([])
                setMonthColorMap({})
                setTotalRecord({})
                setTotalDaySuccess([])
                setTotalDayFail([])
                setTotalCpCode({})
                setTotalOwnerBy({})
                setTotalMonth([])
                setTotalType([])
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, search])

    useEffect(() => {
        const now = new Date()
        const year = now.getFullYear()
        const defaultParams = {
            startDate: `${year}-01-01`,
            endDate: `${year}-12-31`,
        }
        setDefaultSearch(defaultParams)
        setSearch(defaultParams)
    }, [])

    useEffect(() => {
        fetchOptions()
        if (defaultSearch) {
            fetchStatistics()
        }
    }, [fetchOptions, fetchStatistics, defaultSearch])

    const handleSearch = (values: any) => {
        const rangeDate = values?.dateExpired
        if (rangeDate) {
            const startDate = rangeDate[0] ? rangeDate[0]?.format('YYYY-MM-DD') : null
            const endDate = rangeDate[1] ? rangeDate[1]?.format('YYYY-MM-DD') : null
            values.startDate = startDate
            values.endDate = endDate
        }

        setSearch({
            cpCode: values.cpCode,
            ownerBy: values.ownerBy,
            amountType: values.amountType,
            startDate: values.startDate || defaultSearch.startDate,
            endDate: values.endDate || defaultSearch.endDate,
        })
    }

    const handleClearSearch = () => {
        setSearch(defaultSearch)
        formSearch.resetFields()
    }

    return (
        <div>
            <Form
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'start',
                    alignItems: 'center',
                    marginBottom: 20,
                }}
                form={formSearch} onFinish={handleSearch} id="searchForm" layout="vertical" autoComplete="off">
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    flexWrap: 'wrap',
                    gap: 20,
                    width: '100%',
                }}>
                    <Form.Item name="cpCode" label={<b>Đối tác</b>}>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn đối tác"
                            defaultValue={''}
                            options={[
                                { label: 'Tất cả', value: '' },
                                ...companyOptions,
                            ]}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name="ownerBy" label={<b>Nhân sự</b>}>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn nhân sự"
                            defaultValue={''}
                            options={[
                                { label: 'Tất cả', value: '' },
                                ...userOptions,
                            ]}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name="amountType" label="Loại">
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn loại"
                            defaultValue={''}
                            options={[
                                { label: 'Tất cả', value: '' },
                                { label: 'Gia hạn', value: 'SUCCESS' },
                                ...typeNoteCustomerOptions,
                            ]}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    {defaultSearch && (
                        <ConfigProvider locale={vi_VN}>
                            <Form.Item name="dateExpired" label="Khoảng thời gian">
                                <RangePicker
                                    defaultValue={[dayjs(defaultSearch.startDate), dayjs(defaultSearch.endDate)]}
                                    format="YYYY-MM-DD"
                                    picker="date" />
                            </Form.Item>
                        </ConfigProvider>
                    )}
                    <Space>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
                        <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
                    </Space>
                </div>
            </Form>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 40, marginBottom: 20 }}>
                <div style={{
                    textAlign: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                    border: '2px dashed #ec4969',
                    padding: '8px 20px',
                    borderRadius: 10,
                }}>
                    <ApiOutlined /> Tổng cộng: <span style={{
                        fontSize: 20,
                        color: '#ec4969',
                    }}>{formatNumber(totalRecord.total || 0)}</span>
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                    border: '2px dashed #ec4969',
                    padding: '8px 20px',
                    borderRadius: 10,
                }}>
                    <CheckOutlined style={{ color: 'green' }} /> Gia hạn: <span style={{
                        fontSize: 20,
                        color: '#ec4969',
                    }}>{formatNumber(totalRecord.success || 0)}</span>
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                    border: '2px dashed #ec4969',
                    padding: '8px 20px',
                    borderRadius: 10,
                }}>
                    <CloseOutlined style={{ color: 'red' }} /> Từ chối: <span style={{
                        fontSize: 20,
                        color: '#ec4969',
                    }}>{formatNumber(totalRecord.fail || 0)}</span>
                </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
                {!search.cpCode && totalCpCode?.length > 0 && (
                    <div style={{ marginBottom: 40, flex: 1 }}>
                        <ChartAmountType
                            data={totalCpCode}
                            title='Tổng số lượng theo đối tác'
                        />
                    </div>
                )}
                {totalMonth.length > 0 && (
                    <div style={{ marginBottom: 40, flex: 2 }}>
                        <ChartMonth
                            data={totalMonth}
                            title='Tổng số lượng theo tháng'
                        />
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
                {!search.ownerBy && totalOwnerBy?.length > 0 && (
                    <div style={{ marginBottom: 40, flex: 2 }}>
                        <ChartTypeY
                            data={totalOwnerBy}
                            title='Tổng số lượng theo nhân sự'
                        />
                    </div>
                )}
                {!search.amountType && totalType?.length > 0 && (
                    <div style={{ marginBottom: 40, flex: 1 }}>
                        <ChartAmountType
                            data={totalType}
                            title='Khách hàng phản hồi'
                        />
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
                {totalDaySuccess?.length > 0 && (
                    <div style={{ marginBottom: 40, flex: 2 }}>
                        <ChartDaySelect
                            data={totalDaySuccess.filter((item: any) => selectedMonths.includes(item.month))}
                            title='Số lượng gia hạn thành công'
                            monthColorMap={monthColorMap}
                        />
                    </div>
                )}
                {totalDayFail?.length > 0 && (
                    <div style={{ marginBottom: 40, flex: 2 }}>
                        <ChartDaySelect
                            data={totalDayFail.filter((item: any) => selectedMonths.includes(item.month))}
                            title='Số lượng từ chối gia hạn'
                            monthColorMap={monthColorMap}
                        />
                    </div>
                )}
            </div>
            {allMonthsHaving.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginBottom: 20 }}>
                    {allMonthsHaving.map((month: any) => (
                        <Tag key={month} color={monthColorMap[month]}>
                            <Checkbox
                                checked={selectedMonths.includes(month)}
                                onChange={(e) => {
                                    setSelectedMonths(prev => e.target.checked
                                        ? [...prev, month]
                                        : prev.filter(m => m !== month)
                                    )
                                }}
                            >
                                <span style={{ color: 'white' }}>Tháng {month}</span>
                            </Checkbox>
                        </Tag>
                    ))}
                </div>
            )}
        </div>
    )
}
